import React, { useState, useEffect } from 'react';
import { Table, Button, Upload, message, Input, Space, Popconfirm, Progress, Avatar, Row, Col } from 'antd';
import { 
  UploadOutlined, 
  DeleteOutlined, 
  VideoCameraOutlined, 
  SearchOutlined,
  FileTextOutlined,
  PictureOutlined
} from '@ant-design/icons';
import axios from 'axios';
import VideoModal from '../VideoModal';

const MobileMediaLibrary = () => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [videoModalVisible, setVideoModalVisible] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [imageModalVisible, setImageModalVisible] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const fetchFiles = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/box/media/');
      setFiles(response.data);
    } catch (error) {
      message.error('Error loading media');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFiles();
  }, []);

  const handlePreview = (file) => {
    if (file.file_type === 'image') {
      setSelectedImage(file);
      setImageModalVisible(true);
    } else {
      setSelectedVideo(file);
      setVideoModalVisible(true);
    }
  };

  const handleUpload = async (options) => {
    const { file, onSuccess, onError } = options;
    const formData = new FormData();
    const fileType = file.type.startsWith('image/') ? 'image' : 'video';
    formData.append('file', file);
    formData.append('name', file.name);
    formData.append('file_type', fileType);
    formData.append('file_size', file.size);

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const response = await axios.post('/api/box/media/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => setUploadProgress(Math.round((e.loaded * 100) / e.total))
      });
      message.success('Uploaded!');
      setIsUploading(false);
      onSuccess(response.data);
      fetchFiles();
    } catch (error) {
      setIsUploading(false);
      onError(error);
      message.error('Upload error');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/api/box/media/${id}/`);
      message.success('Deleted');
      fetchFiles();
    } catch (error) {
      message.error('Error');
    }
  };

  const filteredFiles = files.filter(f => f.name.toLowerCase().includes(searchText.toLowerCase()));

  const columns = [{
    title: 'Media',
    key: 'media',
    render: (record) => (
      <div className="m-mini-card">
        {/* 1. Thumbnail */}
        <div className="m-card-col thumb-info" onClick={() => handlePreview(record)}>
          {record.file_type === 'image' ? (
            <Avatar shape="square" size={50} src={record.file_url || record.file} icon={<PictureOutlined />} className="media-avatar" />
          ) : (
            <Avatar shape="square" size={50} src={record.thumbnail ? `https://escreen.stand.ledscreen.rs/media/${record.thumbnail}` : null} icon={<VideoCameraOutlined />} className="media-avatar video-bg" />
          )}
        </div>

        {/* 2. Info (Name & Type) */}
        <div className="m-card-col main-info" onClick={() => handlePreview(record)}>
          <div className="box-name-text">{record.name}</div>
          <div className="status-label" style={{ color: record.file_type === 'video' ? '#60a5fa' : '#fbbf24' }}>
            {record.file_type.toUpperCase()}
          </div>
        </div>

        {/* 3. Size */}
        <div className="m-card-col size-info">
          <div className="tiny-label">SIZE</div>
          <div className="date-val">{(record.file_size / 1024 / 1024).toFixed(1)}MB</div>
        </div>

        {/* 4. Action */}
        <div className="m-card-col action-btn">
          <Popconfirm title="Delete?" onConfirm={() => handleDelete(record.id)}>
            <Button 
              type="text" 
              icon={<DeleteOutlined />} 
              className="view-btn-dark del-style"
            />
          </Popconfirm>
        </div>
      </div>
    )
  }];

  return (
    <div className="mobile-dashboard-wrapper">
      <Row gutter={[8, 8]} style={{ marginBottom: '15px' }}>
        <Col span={24}>
          <div className="st-box" style={{ borderBottomColor: '#3b82f6' }}>
            <Space>
              <FileTextOutlined style={{ color: '#3b82f6', fontSize: '20px' }} />
              <span style={{ color: '#ffffff', fontSize: '15px', fontWeight: 'bold' }}>Total Files:</span> 
              <span style={{ color: 'white', fontWeight: '900', fontSize: '22px' }}>{files.length}</span>
            </Space>
          </div>
        </Col>
        <Col span={24}>
          <div className="st-box" style={{ borderBottomColor: '#3b82f6', flexDirection: 'column', gap: '10px' }}>
            <Input 
              placeholder="Search..." 
              prefix={<SearchOutlined />} 
              onChange={e => setSearchText(e.target.value)} 
              style={{ width: '100%', background: '#0f172a', border: '1px solid #334155', color: 'white' }} 
            />
            <Upload customRequest={handleUpload} showUploadList={false} disabled={isUploading} style={{ width: '100%' }}>
              <Button block type="primary" icon={<UploadOutlined />} loading={isUploading} style={{ background: '#3b82f6', fontWeight: 'bold' }}>UPLOAD NEW MEDIA</Button>
            </Upload>
            {isUploading && <Progress percent={uploadProgress} size="small" showInfo={false} strokeColor="#3b82f6" style={{ width: '100%', marginTop: '5px' }} />}
          </div>
        </Col>
      </Row>

      <Table 
        dataSource={filteredFiles} 
        columns={columns} 
        rowKey="id" 
        loading={loading} 
        pagination={{ pageSize: 20, size: 'small' }} 
      />

      <VideoModal isVisible={videoModalVisible} onClose={() => setVideoModalVisible(false)} video={selectedVideo} isImage={false} />
      <VideoModal isVisible={imageModalVisible} onClose={() => setImageModalVisible(false)} video={selectedImage} isImage={true} />

      <style>{`
        .mobile-dashboard-wrapper { padding-bottom: 20px; }
        .st-box { background: #1e293b; padding: 15px; border-radius: 8px; border: 1px solid #334155; border-bottom: 5px solid; display: flex; justify-content: center; align-items: center; }
        
        .ant-table { background: transparent !important; }
        .ant-table-thead { display: none !important; }
        .ant-table-tbody > tr > td { border: none !important; padding: 0 0 10px 0 !important; background: transparent !important; }

        .m-mini-card { 
          background: #1e293b; 
          border: 1px solid #475569; 
          border-radius: 10px; 
          padding: 10px; 
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
        }

        .m-card-col { display: flex; flex-direction: column; min-width: 0; }
        .thumb-info { flex: 0 0 auto; }
        .main-info { flex: 2; margin-left: 4px; }
        .size-info { flex: 1; align-items: flex-start; }
        .action-btn { flex: 0 0 auto; }

        .media-avatar { border: 1px solid #334155; border-radius: 6px !important; }
        .video-bg { background: #0f172a !important; }

        .box-name-text { color: white !important; font-weight: 700; font-size: 14px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .status-label { font-size: 10px; font-weight: 900; letter-spacing: 0.5px; margin-top: 2px; }

        .tiny-label { color: #ffffff !important; font-size: 9px; font-weight: 900; text-transform: uppercase; margin-bottom: 2px; border-bottom: 1px solid rgba(255,255,255,0.1); width: fit-content; }
        .date-val { color: #ffffff !important; font-size: 12px; font-weight: 700; }

        .view-btn-dark {
          background: #1e293b !important; 
          color: #94a3b8 !important; 
          border: 1px solid #334155 !important; 
          border-radius: 6px !important; 
          font-weight: bold !important; 
          height: 34px !important;
          width: 34px !important;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .del-style { color: #ef4444 !important; }

        .ant-pagination-item-active { background: #3b82f6 !important; border-color: #3b82f6 !important; }
        .ant-pagination-item a { color: #94a3b8 !important; }
        .ant-pagination-item-active a { color: white !important; }
      `}</style>
    </div>
  );
};

export default MobileMediaLibrary;